import { useEffect, useMemo, useState } from "react";
import { Image, Text, View } from "react-native";
import type { ImageSourcePropType } from "react-native";
import { AlertCircle, Clock, Shield } from "lucide-react-native";
import {
  CardSurface,
  ClinicalDataStatus,
  Row,
  colors,
  LoadingState,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import { GroupedCard } from "../components";
import { CARE_CONNECTION_ICONS, ICONS, TINTED_ICON_FILTER } from "../icons";
import type {
  ConnectionStatus,
  ConnectionType,
  ConnectionWithSharing,
  YouScreenAction,
} from "../types";
import { useCarePersons } from "@src/packages/patients/hooks/useCarePersons";
import { usePartnerInvitation } from "@src/packages/patients/hooks/usePartnerInvite";
import {
  useCareConnections,
  type CareConnection,
  type CareConnectionKind,
} from "@src/packages/patients/hooks/useCareConnections";
import { useActiveCycle } from "@src/packages/cycles/hooks/useCycles";
import { getDevCareConnectionsOverride } from "../devCareConnections";
import CareConnectionModal from "../modals/CareConnectionModal";

const ICON_SIZE = 28;

const CONNECTION_ICONS: Record<ConnectionType, ImageSourcePropType> = {
  partner: ICONS.connections,
  clinic: ICONS.building,
  pharmacy: ICONS.tablet,
  provider: ICONS.medicalFertility,
};

const CONNECTION_LABEL_KEYS: Record<
  ConnectionType,
  { labelKey: string; addLabelKey: string }
> = {
  partner: {
    labelKey: "you.connections_sharing.partner_label",
    addLabelKey: "you.connections_sharing.partner_add",
  },
  clinic: {
    labelKey: "you.connections_sharing.clinic_label",
    addLabelKey: "you.connections_sharing.clinic_add",
  },
  pharmacy: {
    labelKey: "you.connections_sharing.pharmacy_label",
    addLabelKey: "you.connections_sharing.pharmacy_add",
  },
  provider: {
    labelKey: "you.connections_sharing.provider_label",
    addLabelKey: "you.connections_sharing.provider_add",
  },
};

function ConnectionIcon({ source }: { source: ImageSourcePropType }) {
  return (
    <Image
      source={source}
      style={{ width: ICON_SIZE, height: ICON_SIZE, ...TINTED_ICON_FILTER }}
    />
  );
}

function ConnectionTrailing({
  status,
  type,
  sharingLabel,
}: {
  status: ConnectionStatus;
  type: ConnectionType;
  sharingLabel: string;
}) {
  if (status === "connected") {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
        <Shield size={14} color={EMBER.colors.warm} strokeWidth={1.5} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "500",
            color: EMBER.colors.warm,
          }}
        >
          {sharingLabel}
        </Text>
      </View>
    );
  }

  if (status === "pending") {
    return <Clock size={15} color={EMBER.colors.gold} strokeWidth={1.6} />;
  }

  if (status === "failed") {
    return (
      <AlertCircle size={16} color={EMBER.colors.coral} strokeWidth={1.6} />
    );
  }

  if (type === "partner") {
    // The standard Row chevron already communicates that this launches the
    // partner invitation flow. Repeating an orange "Invite partner" action
    // makes the row read like two competing affordances.
    return null;
  }

  return null;
}

type Props = {
  connections: ConnectionWithSharing[];
  dispatch: React.Dispatch<YouScreenAction>;
  cycleId?: string;
  initialKind?: CareConnectionKind;
  /** Development-only fixture data used by the visual component inventory. */
  careConnectionsOverride?: CareConnection[];
};

export default function ConnectionsSharing({
  connections,
  dispatch,
  cycleId,
  initialKind,
  careConnectionsOverride,
}: Props) {
  const t = useTranslations();
  const { partnerConnected, partnerName } = useCarePersons();
  const { data: invitation } = usePartnerInvitation();
  const { data: activeCycle } = useActiveCycle();
  const cycleContextId =
    cycleId ??
    (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get(
      "dev_care_connections_cycle",
    ) === "1"
      ? activeCycle?.id
      : undefined);
  const {
    data: fetchedCareConnections = [],
    isLoading: careConnectionsLoading,
    isError: careConnectionsError,
    isFetching: careConnectionsFetching,
    refetch: refetchCareConnections,
  } = useCareConnections();
  const devCareConnectionsOverride = getDevCareConnectionsOverride();
  const careConnections =
    careConnectionsOverride ??
    devCareConnectionsOverride ??
    fetchedCareConnections;
  const [careModalKind, setCareModalKind] = useState<CareConnectionKind | null>(
    null,
  );
  const [selectedCareConnectionId, setSelectedCareConnectionId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const devRequestedKind =
      __DEV__ && typeof window !== "undefined" && window.location
        ? new URLSearchParams(window.location.search).get(
            "dev_open_care_connection",
          )
        : null;
    const requestedKind = devRequestedKind ?? initialKind;
    if (
      requestedKind === "clinic" ||
      requestedKind === "pharmacy" ||
      requestedKind === "provider"
    ) {
      setCareModalKind(requestedKind);
    }
  }, [initialKind]);

  const clinicProviderId = activeCycle?.providerId ?? null;
  const clinicName = activeCycle?.provider ?? null;
  const clinicConnected = !!clinicProviderId;

  const careConnectionsByKind = useMemo(() => {
    const grouped = new Map<CareConnectionKind, CareConnection[]>();
    for (const connection of careConnections) {
      const current = grouped.get(connection.kind) ?? [];
      current.push(connection);
      grouped.set(connection.kind, current);
    }
    return grouped;
  }, [careConnections]);

  const latestCareConnectionByKind = useMemo(
    () =>
      new Map(
        [...careConnectionsByKind].flatMap(([kind, records]) =>
          records[0] ? [[kind, records[0]] as const] : [],
        ),
      ),
    [careConnectionsByKind],
  );

  const apiConnections = connections.map((connection) => {
    const careConnection =
      connection.type === "partner"
        ? null
        : (latestCareConnectionByKind.get(connection.type) ?? null);

    if (connection.type === "partner") {
      if (partnerConnected) {
        return {
          ...connection,
          careConnection,
          status: "connected" as const,
          name: partnerName ?? connection.name,
        };
      }
      if (invitation?.status === "pending") {
        return {
          ...connection,
          careConnection,
          status: "pending" as const,
          name: invitation.inviteeFirstName ?? connection.name,
        };
      }
    }

    if (connection.type === "clinic" && clinicConnected) {
      return {
        ...connection,
        // An active provider link is a real connection and takes precedence
        // over an older intent request for a different clinic.
        careConnection: null,
        status: "connected" as const,
        name: clinicName,
      };
    }

    return { ...connection, careConnection };
  });

  const handlePress = (connection: ConnectionWithSharing) => {
    if (connection.type === "partner") {
      if (connection.status === "connected") {
        dispatch({
          type: "SHOW_DATA_SHARING_MODAL",
          connectionType: "partner",
        });
      } else {
        if (connection.status === "pending") {
          dispatch({
            type: "SHOW_DATA_SHARING_MODAL",
            connectionType: "partner",
          });
        } else {
          dispatch({ type: "SHOW_MODAL", modal: "partner-invite" });
        }
      }
      return;
    }

    if (connection.type === "clinic" && connection.status === "connected") {
      dispatch({
        type: "SHOW_DATA_SHARING_MODAL",
        connectionType: "clinic",
      });
      return;
    }

    setSelectedCareConnectionId(null);
    setCareModalKind(connection.type);
  };

  const openCareConnection = (
    kind: CareConnectionKind,
    connectionId: string | null = null,
  ) => {
    setSelectedCareConnectionId(connectionId);
    setCareModalKind(kind);
  };

  return (
    <GroupedCard>
      {careConnectionsError &&
      !careConnectionsOverride &&
      !devCareConnectionsOverride ? (
        <View
          style={{
            paddingHorizontal: 16,
            paddingTop: 14,
            paddingBottom: 4,
            gap: 8,
          }}
        >
          <ClinicalDataStatus
            state="stale"
            message={t("you.connections_sharing.load_failed")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={() => void refetchCareConnections()}
            isRetrying={careConnectionsFetching}
            testID="care-connections-stale"
          />
        </View>
      ) : null}
      {careConnectionsLoading &&
      !careConnectionsOverride &&
      !devCareConnectionsOverride ? (
        <LoadingState
          caption={t("you.connections_sharing.loading")}
          style={{ paddingVertical: 12 }}
        />
      ) : null}
      {apiConnections.map((connection, index) => {
        const config = CONNECTION_LABEL_KEYS[connection.type];
        const careConnection = connection.careConnection;
        const careConnectionRecords =
          connection.type === "partner"
            ? []
            : (careConnectionsByKind.get(connection.type) ?? []);
        const hasMultipleCareConnections = careConnectionRecords.length > 1;
        const isInteractive = true;
        const careRecordSubtitle = (record: CareConnection) =>
          record.role ??
          record.specialty ??
          record.practiceName ??
          record.location ??
          t("you.connections_sharing.care_team_member");
        const title =
          connection.status === "connected" && connection.name
            ? connection.name
            : hasMultipleCareConnections
              ? t(
                  `you.connections_sharing.connection_summary.${connection.type}_multiple`,
                  { params: { count: String(careConnectionRecords.length) } },
                )
              : (careConnection?.name ?? t(config.labelKey));
        const subtitle =
          connection.status === "connected"
            ? t("you.connections_sharing.connected")
            : hasMultipleCareConnections
              ? t(
                  "you.connections_sharing.connection_summary.multiple_subtitle",
                )
              : careConnection
                ? careRecordSubtitle(careConnection)
                : connection.status === "pending"
                  ? t("you.connections_sharing.pending")
                  : connection.status === "failed"
                    ? t("you.connections_sharing.failed")
                    : connection.type === "partner"
                      ? t(config.addLabelKey)
                      : t(`you.connections_sharing.${connection.type}_detail`);

        if (hasMultipleCareConnections) {
          if (connection.type === "partner") return null;
          const careKind = connection.type;
          return (
            <View
              key={connection.type}
              style={{
                borderTopWidth: index > 0 ? 1 : 0,
                borderTopColor: colors.border,
              }}
            >
              <Row
                surface="plain"
                leading={
                  <ConnectionIcon source={CONNECTION_ICONS[connection.type]} />
                }
                leadingSize={ICON_SIZE}
                title={title}
                subtitle={subtitle}
                chevron
                onPress={() => openCareConnection(careKind)}
                action="connection_group_row"
                accessibilityLabel={`${title}. ${subtitle}`}
                gap={14}
                paddingHorizontal={16}
                paddingVertical={12}
                borderRadius={0}
                titleStyle={{
                  fontSize: 15,
                  lineHeight: 20,
                  fontWeight: "500",
                  color: EMBER.colors.text,
                }}
                subtitleStyle={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: EMBER.colors.textLight,
                }}
              />
              <View
                style={{
                  gap: 8,
                  paddingHorizontal: 12,
                  paddingBottom: 12,
                }}
              >
                {careConnectionRecords.map((record) => (
                  <CardSurface
                    key={record.id}
                    variant="panel"
                    style={{ overflow: "hidden" }}
                  >
                    <Row
                      surface="plain"
                      leading={
                        <ConnectionIcon
                          source={CONNECTION_ICONS[connection.type]}
                        />
                      }
                      leadingSize={ICON_SIZE}
                      title={record.name}
                      subtitle={careRecordSubtitle(record)}
                      chevron
                      onPress={() => openCareConnection(careKind, record.id)}
                      action="care_connection_row"
                      accessibilityLabel={record.name}
                      gap={12}
                      paddingHorizontal={14}
                      paddingVertical={10}
                      borderRadius={12}
                      titleStyle={{
                        fontSize: 14,
                        lineHeight: 18,
                        fontWeight: "500",
                        color: EMBER.colors.text,
                      }}
                      subtitleStyle={{
                        fontSize: 12,
                        lineHeight: 16,
                        color: EMBER.colors.textLight,
                      }}
                    />
                  </CardSurface>
                ))}
              </View>
            </View>
          );
        }

        return (
          <Row
            key={connection.type}
            testID={`connection-row-${connection.type}`}
            surface="plain"
            leading={
              <ConnectionIcon source={CONNECTION_ICONS[connection.type]} />
            }
            leadingSize={ICON_SIZE}
            title={title}
            subtitle={subtitle}
            trailing={
              <ConnectionTrailing
                status={connection.status}
                type={connection.type}
                sharingLabel={t("you.connections_sharing.sharing")}
              />
            }
            chevron={isInteractive}
            onPress={isInteractive ? () => handlePress(connection) : undefined}
            action="connection_row"
            accessibilityLabel={`${title}. ${subtitle}`}
            gap={14}
            paddingHorizontal={16}
            paddingVertical={12}
            borderRadius={0}
            titleStyle={{
              fontSize: 15,
              lineHeight: 20,
              fontWeight: "500",
              color: EMBER.colors.text,
            }}
            subtitleStyle={{
              fontSize: 12,
              lineHeight: 16,
              color: EMBER.colors.textLight,
            }}
            style={{
              borderRadius: 0,
              borderTopWidth: index > 0 ? 1 : 0,
              // Match the shared, quiet divider treatment used by the
              // Medical & Fertility dialog rows.
              borderTopColor: colors.border,
            }}
          />
        );
      })}

      <CareConnectionModal
        visible={careModalKind !== null}
        kind={careModalKind}
        cycleId={cycleContextId}
        connections={
          careModalKind ? (careConnectionsByKind.get(careModalKind) ?? []) : []
        }
        selectedConnectionId={selectedCareConnectionId}
        icon={
          careModalKind
            ? CARE_CONNECTION_ICONS[careModalKind]
            : CARE_CONNECTION_ICONS.clinic
        }
        onClose={() => setCareModalKind(null)}
      />
    </GroupedCard>
  );
}
